// kalanjay/pages/KalanjayAgeGroups.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Stack,
  InputAdornment,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import * as yup from "yup";
import SearchIcon from "@mui/icons-material/Search";
import AppTable from "../../../common/app-table";
import DashboardBadge from "../../../common/dashboard-badges";
import ButtonOne from "../../../common/normal-button";
import AppDatePicker from "../../../common/app-date-picker";
import AppChip from "../../../common/app-chip";
import typography from "../../../cssUtils/typography";
import {
  ColorsConstants,
  grayFontColor,
  dashboardCardGreenTextColor,
  inputColor,
  borderColors,
} from "../../../cssUtils/constants";
import { useAuth } from "../../../hooks/AuthProvider";
import NoPermission from "../../../common/NoPermission";
import CustomTabs from "../../../common/custom-tabs";
import {
  GET_KALANJAY_AGE_GROUPS,
  GET_KALANJAY_CENTRES,
} from "../../../config/urls";
import axios from "axios";

// ══════════════════════════════════════════════════════════════════════════════
// KalanjayAgeGroups
// Props:
//   ageGroups / setAgeGroups — state + setter
//   allBatches             — array of all batch objects (for assign dropdown)
//   showToast              — (msg) => void
// ══════════════════════════════════════════════════════════════════════════════

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    height: 40,
    ...typography.bodyLarge,
    color: inputColor,
    "& fieldset": { borderColor: borderColors.inputFieldGrey },
    "&:hover fieldset": { borderColor: ColorsConstants.primaryGray50 },
    "&.Mui-focused fieldset": { borderColor: ColorsConstants.primaryPurple100 },
  },
  "& .MuiInputLabel-root": { ...typography.bodyLarge },
};

const KalanjayAgeGroups = ({ setSelectedAgeGroup, setSelectedAgeGroupName, setSelectedCentreId }) => {
  const { getValidToken } = useAuth();
  const permissions = localStorage.getItem("permissions") || [];
  const hasViewPermission = permissions.includes("waitlist.view");

  const [ageGroups, setAgeGroups] = useState([]);
  const [centres, setCentres] = useState([]);
  const [cardData, setCardData] = useState({});
  const [search, setSearch] = useState("");
  const [centreTab, setCentreTab] = useState("");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchCentres();
  }, []);

  useEffect(() => {
    if (centres.length > 0 && !centreTab) {
      const firstCentreId = centres[0].id;
      setCentreTab(firstCentreId);
      setPage(0);
      fetchAgeGroups(rowsPerPage, 0, firstCentreId);
    }
  }, [centres]);

  // const onSearch = () => {
  //   fetchAgeGroups(rowsPerPage, 0, centreTab, search);
  // }

  const handleSearchChange = (val) => {
    setSearch(val);
    fetchAgeGroups(rowsPerPage, 0, centreTab, val);
  };  

  const fetchAgeGroups = async (
    limit = 20,
    offset = 0,
    centreId = "",
    searchVal = search,
  ) => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const params = new URLSearchParams({
        limit,
        offset,
        is_search: Boolean(searchVal),
        ...(searchVal && { search: searchVal }),
        ...(centreId && { centre: centreId }),
      });

      const res = await axios.get(
        `${GET_KALANJAY_AGE_GROUPS}?${params.toString()}`,
        { headers },
      );
      const data = res?.data?.results || res.data;
      const count = res?.data?.count ?? data.length;

      setAgeGroups(data);
      setTotalCount(count);
      setCardData({
        total_waitlist_count: res?.data?.total_waitlist_count,
        total_juhu_waitlist_count: res?.data?.total_juhu_waitlist_count,
        total_grant_road_waitlist_count:
          res?.data?.total_grant_road_waitlist_count,
        centres: res?.data?.centres,
      });
    } catch (err) {
      console.error("Error fetching age groups:", err);
      return [];
    }
  };

  const fetchCentres = async () => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    try {
      const response = await axios.get(GET_KALANJAY_CENTRES, {
        headers: headers,
      });

      const centresFromApi = response?.data?.results;
      setCentres(centresFromApi);
    } catch (error) {
      console.log(error);
    }
  };

  const onPageChange = (_, newPage) => {
    setPage(newPage);
    fetchAgeGroups(rowsPerPage, newPage * rowsPerPage, centreTab, search);
  };

  const onRowsPerPageChange = (e) => {
    const val = Number(e?.target?.value);
    if (!val || isNaN(val)) return;
    setRowsPerPage(val);
    setPage(0);
    fetchAgeGroups(val, 0, centreTab, search);
  };

  const onCentreChange = (centreId) => {
    setPage(0);
    fetchAgeGroups(rowsPerPage, 0, centreId, search);
  };

  const centreTabs = centres.map((c) => ({
    label: `${c.name}`,
    value: c.id,
  }));

  // ── table columns ─────────────────────────────────────────────────────────
  const columns = [
    {
      id: "age_group",
      label: "Age Group",
      render: (r) => (
        <Box sx={{ display: "flex", alignItems: { xs: "stretch", md: "center" }, gap: 1.5 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: ColorsConstants.primaryPurple100,
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              ...typography.bodyLarge,
              color: ColorsConstants.primaryPurple100,
              cursor: "pointer",
              textDecoration: "underline",
              textDecorationColor: ColorsConstants.primaryPurple100 + "60",
            }}
            onClick={() => {
              setSelectedAgeGroup(r.id);
              setSelectedAgeGroupName(r.name);
              setSelectedCentreId?.(centreTab);
            }}
          >
            {r.name}
          </Typography>
        </Box>
      ),
    },
    {
      id: "count",
      label: "Count",
      render: (r) => (
        <Typography sx={typography.bodyLarge}>{r.count}</Typography>
      ),
    },
  ];

  if (!hasViewPermission) {
    return (
      <Stack sx={{ mt: 16 }}>
        <NoPermission />
      </Stack>
    );
  }

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* <Typography sx={{ ...typography.h1, mb: 3 }}>Waitlist</Typography> */}

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          {
            value: cardData?.total_waitlist_count || 0,
            label: "All entries",
            textColor: ColorsConstants.primaryBackgroundColor,
            title: "Total Waitlist",
          },
          {
            value: cardData?.total_juhu_waitlist_count || 0,
            label: "All entries",
            textColor: "#C07000",
            title: "Juhu Waitlist",
          },
          {
            value: cardData?.total_grant_road_waitlist_count || 0,
            label: "All entries",
            textColor: dashboardCardGreenTextColor,
            title: "Grant Road Waitlist",
          },
        ].map((item) => (
          <Grid item xs={12} sm={6} md={4} key={item.label}>
            <DashboardBadge
              title={item.title}
              value={item.value}
              label={item.label}
              textColor={item.textColor}
            />
          </Grid>
        ))}
      </Grid>

      {/* Table header */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: { xs: "flex-start", md: "space-between" },
          mb: 1.5,
        }}
      >
        <Typography sx={typography.cardTitle}>
          Age Groups{" "}
          <Typography
            component="span"
            sx={{ ...typography.bodyLarge, color: grayFontColor }}
          >
            ({ageGroups.length} / {totalCount})
          </Typography>
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "flex-start", md: "space-between" },
          flexWrap: "wrap",
          alignItems: { xs: "stretch", md: "center" },
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ mb: 2, width: "100%", flex: 1, minWidth: 0 }}>
          <CustomTabs
            value={centreTab}
            onChange={(_, v) => {
              setCentreTab(v);  
              onCentreChange(v);
              setSearch("");
            }}
            tabs={centreTabs}
            sx={{ width: "100%", maxWidth: "100%", flex: 1, minWidth: 0 }}
          />
        </Box>
        <TextField
          size="small"
          placeholder="Search by parent name, child name, phone…"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: grayFontColor, fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{ ...fieldSx, width: { xs: "100%", sm: 300, md: 360 } }}
        />
      </Box>

      <AppTable
        columns={columns}
        rows={ageGroups}
        rowKey="id"
        emptyMessage="No age group entries yet."
        maxHeight={600}
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Box>
  );
};

export default KalanjayAgeGroups;
